import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  OverlayTrigger,
  Row,
  Spinner,
  Tooltip
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAt,
  faClock,
  faExclamationTriangle,
  faInfoCircle,
  faLayerGroup,
  faLink,
  faPlus,
  faRedoAlt,
  faSyncAlt,
  faUserFriends
} from '@fortawesome/free-solid-svg-icons';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableSearchBox from 'components/common/advance-table/AdvanceTableSearchBox';
import CommonBreadCrumb from 'components/commonbreadcrumb/CommonBreadCrumb';
import ThemedSelect from 'components/commonbreadcrumb/ThemedSelect';
import RegistrationQueueShimmer from 'components/ShimmerUI/RegistrationQueueShimmer';
import useAdvanceTable from 'hooks/useAdvanceTable';
import useRequestState from 'hooks/useRequestState';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import paths from 'routes/paths';
import {
  NETWORK_ERROR_MESSAGE,
  addAllowedDomain,
  getApiErrorMessage,
  listAllowedDomains,
  reactivateAllowedDomain
} from 'helpers/registrationApi';
import {
  normalizeDomain,
  suggestWithoutWww,
  validateDomain
} from 'helpers/emailDomainRules';
import {
  getCachedGroupNameMap,
  getCachedGroupOptions
} from 'components/admin/facility-staff/add-facility-staff/staffFormRules';
import {
  StateMessage,
  TonePill,
  ToneIcon
} from 'components/registration-shared/RegistrationUI';
import {
  EmailDomainsIllustration,
  QueueErrorIllustration
} from 'components/registration-shared/RegistrationIllustrations';
import {
  formatDateTime,
  formatRelative,
  toMoment
} from 'components/registration-shared/registrationFormat';
import DeactivateDomainModal from './DeactivateDomainModal';

// Numeric-looking ids as numbers, like the rest of the admin screens.
const toId = value => (/^\d+$/.test(String(value)) ? Number(value) : value);

const hideBelow = breakpoint => `d-none d-${breakpoint}-table-cell`;

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

/* -------------------------------------------------------------------------- */
/*                                Add a domain                                */
/* -------------------------------------------------------------------------- */

const AddDomainForm = ({ groupOptions, activeRows, onAdded }) => {
  const [group, setGroup] = useState(null);
  const [domainText, setDomainText] = useState('');
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [serverError, setServerError] = useState('');
  const addReq = useRequestState();
  const adding = addReq.isLoading;
  const domainInputRef = useRef(null);

  const domain = normalizeDomain(domainText);
  const domainError = validateDomain(domain, activeRows);
  const groupError = group ? '' : 'Choose the group this domain belongs to.';
  const wwwSuggestion = domainError ? '' : suggestWithoutWww(domain);
  const rewritten =
    !domainError && domain !== domainText.trim().toLowerCase() ? domain : '';

  const showDomainError =
    (submitAttempted || touched.domain) && domainText.trim() && domainError;
  const showEmptyDomainError =
    submitAttempted && !domainText.trim() && domainError;
  const showGroupError = (submitAttempted || touched.group) && groupError;

  const domainCounts = useMemo(() => {
    const counts = new Map();
    activeRows.forEach(row => {
      const key = String(row.group_id);
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return counts;
  }, [activeRows]);

  const handleSubmit = async e => {
    e.preventDefault();
    if (adding) {
      return;
    }
    setSubmitAttempted(true);
    setServerError('');
    if (groupError || domainError) {
      if (groupError) {
        document.getElementById('domain-group')?.focus();
      } else {
        domainInputRef.current?.focus();
      }
      return;
    }

    const result = await addReq.run(() =>
      addAllowedDomain({ groupId: toId(group.value), domain })
    );
    if (result.stale) {
      return;
    }
    if (!result.ok) {
      const message = getApiErrorMessage(result.error, NETWORK_ERROR_MESSAGE);
      setServerError(message);
      toast.error(message);
      return;
    }
    if (!result.data.ok) {
      const message = result.data.message || 'Unable to add this domain.';
      setServerError(message);
      toast.error(message);
      return;
    }

    toast.success(
      <div>
        <div className="fw-bold">{domain} added</div>
        <div className="fs-10">
          New registrations from this domain are linked to {group.label}.
        </div>
      </div>,
      { theme: 'colored' }
    );
    // Keep the group: admins often add several domains for one group.
    setDomainText('');
    setTouched({});
    setSubmitAttempted(false);
    onAdded();
    domainInputRef.current?.focus();
  };

  return (
    <Form noValidate onSubmit={handleSubmit}>
      <Row className="g-3 align-items-start">
        <Col md={5}>
          <Form.Label htmlFor="domain-group" className="required">
            Group
          </Form.Label>
          <div
            className={classNames('mym-select mym-select-sm', {
              'is-invalid': !!showGroupError
            })}
          >
            <ThemedSelect
              inputId="domain-group"
              options={groupOptions}
              value={group}
              onChange={option => {
                setGroup(option || null);
                setTouched(prev => ({ ...prev, group: true }));
                setServerError('');
              }}
              onBlur={() => setTouched(prev => ({ ...prev, group: true }))}
              placeholder="Choose a group"
              noOptionsMessage={() => 'No groups found'}
              formatOptionLabel={(option, { context }) =>
                context === 'menu' ? (
                  <div className="d-flex justify-content-between gap-2">
                    <span>{option.label}</span>
                    {domainCounts.get(String(option.value)) ? (
                      <span className="fs-11 text-500 text-nowrap">
                        {plural(
                          domainCounts.get(String(option.value)),
                          'domain',
                          'domains'
                        )}
                      </span>
                    ) : null}
                  </div>
                ) : (
                  option.label
                )
              }
            />
          </div>
          {showGroupError && (
            <div className="invalid-feedback d-block">{groupError}</div>
          )}
          {groupOptions.length === 0 && (
            <div className="mym-field-hint is-warning">
              <FontAwesomeIcon icon={faExclamationTriangle} />
              The group list isn't loaded. Open the Dashboard once, then come
              back.
            </div>
          )}
        </Col>

        <Col md={5}>
          <Form.Label htmlFor="domain-name" className="required">
            Email domain
          </Form.Label>
          <InputGroup
            size="sm"
            className={classNames('mym-domain-input', {
              'is-invalid': !!(showDomainError || showEmptyDomainError)
            })}
          >
            <InputGroup.Text>name@</InputGroup.Text>
            <Form.Control
              id="domain-name"
              ref={domainInputRef}
              value={domainText}
              placeholder="bamsi.org"
              autoComplete="off"
              spellCheck={false}
              onChange={e => {
                setDomainText(e.target.value);
                setServerError('');
              }}
              onBlur={() => setTouched(prev => ({ ...prev, domain: true }))}
              isInvalid={!!(showDomainError || showEmptyDomainError)}
            />
          </InputGroup>
          {(showDomainError || showEmptyDomainError) && (
            <div className="invalid-feedback d-block">{domainError}</div>
          )}
          {!domainError && wwwSuggestion && (
            <div className="mym-field-hint is-warning">
              <FontAwesomeIcon icon={faExclamationTriangle} />
              Email addresses rarely start with www.{' '}
              <Button
                variant="link"
                size="sm"
                className="p-0 fs-11 align-baseline"
                onClick={() => setDomainText(wwwSuggestion)}
              >
                Use {wwwSuggestion}
              </Button>
            </div>
          )}
          {!domainError && !wwwSuggestion && rewritten && (
            <div className="mym-field-hint">
              <span>
                It will be saved as <strong>{rewritten}</strong>.
              </span>
            </div>
          )}
        </Col>

        <Col md={2} className="d-grid">
          <Form.Label className="d-none d-md-block invisible">Add</Form.Label>
          <Button type="submit" variant="primary" size="sm" disabled={adding}>
            {adding ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Add domain
              </>
            )}
          </Button>
        </Col>
      </Row>

      {!domainError && group && (
        <div className="mym-inline-note mym-tone-success mt-3 mb-0">
          <FontAwesomeIcon icon={faLink} className="mt-1" />
          <div>
            Requests from <strong>name@{domain}</strong> emails will go to{' '}
            <strong>{group.label}</strong>. Applicants won't be asked for their
            agency's name.
          </div>
        </div>
      )}

      {serverError && (
        <div className="mym-inline-note mym-tone-danger mt-3 mb-0">
          <FontAwesomeIcon icon={faExclamationTriangle} className="mt-1" />
          <div className="fw-semibold">{serverError}</div>
        </div>
      )}
    </Form>
  );
};

AddDomainForm.propTypes = {
  groupOptions: PropTypes.array.isRequired,
  activeRows: PropTypes.array.isRequired,
  onAdded: PropTypes.func.isRequired
};

/* -------------------------------------------------------------------------- */
/*                                    Page                                    */
/* -------------------------------------------------------------------------- */

/**
 * Email domains that identify a group's staff on self-registration
 * (/main/admin/email-domains, roles 1–3). A registration whose email domain is
 * on this list is linked to that group, and the applicant isn't asked for an
 * agency name.
 *
 * Status is explicit, like users in User Management: every row has an
 * Active / Inactive switch. Switching off asks for confirmation first. A
 * status dropdown (Active by default) filters the list.
 */
const EmailDomains = () => {
  const listReq = useRequestState({
    initialStatus: 'loading',
    initialData: []
  });
  const { run: runList, setData: setRows } = listReq;
  const rows = useMemo(() => listReq.data || [], [listReq.data]);
  const [loadedOnce, setLoadedOnce] = useState(false);
  // 'active' | 'inactive' | 'all'
  const [statusFilter, setStatusFilter] = useState('active');
  const [deactivateTarget, setDeactivateTarget] = useState({
    show: false,
    row: null
  });
  const [activatingId, setActivatingId] = useState(null);

  const groupOptions = useMemo(() => getCachedGroupOptions(), []);
  const groupNames = useMemo(() => getCachedGroupNameMap(), []);
  const getGroupName = useCallback(
    row =>
      row?.group_name ||
      groupNames.get(String(row?.group_id)) ||
      (row?.group_id ? `Group #${row.group_id}` : '—'),
    [groupNames]
  );

  const load = useCallback(async () => {
    const result = await runList(listAllowedDomains, { keepData: true });
    if (!result.stale) {
      setLoadedOnce(true);
    }
  }, [runList]);

  useEffect(() => {
    load();
  }, [load]);

  const activeRows = useMemo(() => rows.filter(row => row.is_active), [rows]);
  // A domain that was switched off and later linked again is listed once, as
  // active: the old inactive row is history, not a second domain.
  const inactiveRows = useMemo(() => {
    const activeDomains = new Set(activeRows.map(row => row.domain));
    return rows.filter(row => !row.is_active && !activeDomains.has(row.domain));
  }, [rows, activeRows]);
  const visibleRows = useMemo(() => {
    if (statusFilter === 'all') {
      return [...activeRows, ...inactiveRows];
    }
    return statusFilter === 'inactive' ? inactiveRows : activeRows;
  }, [statusFilter, activeRows, inactiveRows]);
  const groupsCovered = new Set(activeRows.map(row => String(row.group_id)))
    .size;
  const signUps = activeRows.reduce((sum, row) => sum + row.claim_count, 0);

  const setRowActive = (row, isActive) =>
    setRows(
      rows.map(item =>
        item.id === row.id ? { ...item, is_active: isActive } : item
      )
    );

  const handleDeactivated = row => {
    setDeactivateTarget(prev => ({ ...prev, show: false }));
    toast.success(`${row.domain} is now inactive.`, { theme: 'colored' });
    // Show the new status at once, then re-fetch to stay in step with the server.
    setRowActive(row, false);
    load();
  };

  const handleActivate = async row => {
    const taken = activeRows.find(item => item.domain === row.domain);
    if (taken) {
      toast.error(
        `${row.domain} is already active for ${getGroupName(taken)}.`
      );
      return;
    }
    setActivatingId(row.id);
    try {
      const response = await reactivateAllowedDomain({
        groupId: toId(row.group_id),
        domain: row.domain
      });
      if (!response.ok) {
        toast.error(response.message || 'Unable to activate this domain.');
        return;
      }
      toast.success(
        <div>
          <div className="fw-bold">{row.domain} is active</div>
          <div className="fs-10">
            New requests from this domain go to {getGroupName(row)}.
          </div>
        </div>,
        { theme: 'colored' }
      );
      setRowActive(row, true);
      load();
    } catch (error) {
      toast.error(getApiErrorMessage(error, NETWORK_ERROR_MESSAGE));
    } finally {
      setActivatingId(null);
    }
  };

  /* -------------------------------- columns -------------------------------- */

  const columns = [
    {
      id: 'domain',
      accessorFn: row => row.domain,
      header: 'Domain',
      meta: {
        headerProps: { className: 'text-900 ps-3' },
        cellProps: { className: 'ps-3' }
      },
      cell: ({ row: { original } }) => (
        <>
          <span
            className={classNames('mym-domain-name', {
              'is-inactive': !original.is_active
            })}
          >
            <FontAwesomeIcon icon={faAt} className="mym-domain-icon" />
            {original.domain}
          </span>
          {/* Phones: the group goes under the domain instead of its own column. */}
          <div className="d-sm-none fs-11 text-600 mt-1">
            <FontAwesomeIcon icon={faLayerGroup} className="text-500 me-1" />
            {getGroupName(original)}
          </div>
        </>
      )
    },
    {
      id: 'group',
      accessorFn: row => getGroupName(row),
      header: 'Group',
      meta: {
        headerProps: { className: `text-900 ${hideBelow('sm')}` },
        cellProps: { className: hideBelow('sm') }
      },
      cell: ({ getValue }) => (
        <span className="text-nowrap">
          <FontAwesomeIcon
            icon={faLayerGroup}
            className="text-500 me-2 fs-11"
          />
          {getValue()}
        </span>
      )
    },
    {
      id: 'claims',
      accessorFn: row => row.claim_count,
      header: () => (
        <span className="text-nowrap">
          Sign-ups
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip style={{ position: 'fixed' }}>
                Registrations that came in with this domain so far.
              </Tooltip>
            }
          >
            <span className="ms-1">
              <FontAwesomeIcon icon={faInfoCircle} className="text-500" />
            </span>
          </OverlayTrigger>
        </span>
      ),
      meta: {
        headerProps: { className: `text-900 ${hideBelow('md')}` },
        cellProps: { className: hideBelow('md') }
      },
      cell: ({ getValue }) =>
        getValue() > 0 ? (
          <TonePill tone="success" icon={faUserFriends}>
            {getValue()}
          </TonePill>
        ) : (
          <span className="fs-11 text-500">None yet</span>
        )
    },
    {
      id: 'created',
      accessorFn: row => toMoment(row.created)?.valueOf() ?? 0,
      header: 'Added',
      meta: {
        headerProps: { className: `text-900 ${hideBelow('lg')}` },
        cellProps: { className: hideBelow('lg') }
      },
      cell: ({ row: { original } }) =>
        original.created ? (
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip style={{ position: 'fixed' }}>
                {formatDateTime(original.created)}
              </Tooltip>
            }
          >
            <span className="d-inline-flex">
              <TonePill tone="secondary" icon={faClock}>
                {formatRelative(original.created)}
              </TonePill>
            </span>
          </OverlayTrigger>
        ) : (
          <span className="text-400">—</span>
        )
    },
    {
      // Same control as "Is Active" on a user: a labelled switch, never an icon.
      id: 'status',
      accessorFn: row => (row.is_active ? 'Active' : 'Inactive'),
      header: 'Status',
      meta: {
        headerProps: { className: 'text-900 pe-3' },
        cellProps: { className: 'pe-3' }
      },
      cell: ({ row: { original } }) => {
        const activating = activatingId === original.id;
        return (
          <Form.Check
            type="switch"
            id={`domain-status-${original.id}`}
            className="mb-0 text-nowrap"
            checked={original.is_active || activating}
            disabled={activatingId !== null}
            onChange={e =>
              e.target.checked
                ? handleActivate(original)
                : setDeactivateTarget({ show: true, row: original })
            }
            label={
              activating ? (
                <>
                  Active
                  <Spinner
                    animation="border"
                    size="sm"
                    className="ms-2"
                    aria-hidden="true"
                  />
                </>
              ) : original.is_active ? (
                'Active'
              ) : (
                'Inactive'
              )
            }
          />
        );
      }
    }
  ];

  const table = useAdvanceTable({
    data: visibleRows,
    columns,
    selection: false,
    sortable: true,
    pagination: true,
    perPage: 1000,
    initialState: { sorting: [{ id: 'group', desc: false }] }
  });
  const visibleRowCount = table.getRowModel().rows.length;

  /* --------------------------------- render -------------------------------- */

  const breadcrumbItems = [
    { name: 'Dashboard', href: '/main', isActive: false },
    { name: 'User Management', href: paths.viewFacility, isActive: false },
    { name: 'Email Domains', href: paths.adminEmailDomains, isActive: true }
  ];

  let content;
  if (!loadedOnce && listReq.isLoading) {
    content = <RegistrationQueueShimmer rows={4} />;
  } else if (listReq.isError && rows.length === 0) {
    content = (
      <StateMessage
        art={<QueueErrorIllustration width={170} />}
        title="We couldn't load the email domains"
        actions={
          <Button variant="primary" onClick={load}>
            <FontAwesomeIcon icon={faRedoAlt} className="me-2" />
            Try again
          </Button>
        }
      >
        {getApiErrorMessage(listReq.error, NETWORK_ERROR_MESSAGE)}
      </StateMessage>
    );
  } else if (rows.length === 0) {
    content = (
      <StateMessage
        art={<EmailDomainsIllustration width={170} />}
        title="No email domains yet"
      >
        Link a group's work email domain above. Requests from that domain will
        go to the group automatically.
      </StateMessage>
    );
  } else if (visibleRows.length === 0) {
    content = (
      <div className="text-center text-600 fs-10 py-4">
        {statusFilter === 'inactive'
          ? 'No inactive domains.'
          : 'No active domains.'}
      </div>
    );
  } else {
    content = (
      <>
        {listReq.isError && (
          <div className="px-3 pt-3">
            <div className="mym-inline-note mym-tone-warning">
              <FontAwesomeIcon icon={faInfoCircle} className="mt-1" />
              <div className="flex-1">
                Couldn't refresh — showing the last loaded list.{' '}
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 align-baseline fw-semibold"
                  onClick={load}
                >
                  Try again
                </Button>
              </div>
            </div>
          </div>
        )}
        <AdvanceTable
          headerClassName="bg-200 text-nowrap align-middle"
          rowClassName="align-middle"
          tableProps={{ size: 'sm', striped: true, className: 'fs-10 mb-0' }}
        />
        {visibleRowCount === 0 && (
          <div className="text-center text-600 fs-10 py-4">
            No domains match your search.
          </div>
        )}
      </>
    );
  }

  return (
    <div className="mym-queue">
      <CommonBreadCrumb breadcrumbItems={breadcrumbItems} />

      <Card className="mym-queue-hero mb-3 shadow-sm">
        <Card.Body className="p-3 p-md-4">
          <div className="d-flex flex-wrap align-items-center gap-3 mb-3 mb-md-4">
            <ToneIcon
              icon={faAt}
              tone="primary"
              size="xl"
              solid
              className="d-none d-sm-inline-flex"
            />
            <div className="flex-1 min-w-0">
              <h1 className="mym-queue-title">Email domains</h1>
              <p className="mym-queue-sub">
                Requests from an active domain go to its group automatically.
                Other applicants are asked for their agency's name.
              </p>
            </div>
            <Button
              variant="falcon-default"
              size="sm"
              onClick={load}
              disabled={listReq.isLoading}
              aria-label="Refresh"
            >
              <FontAwesomeIcon
                icon={faSyncAlt}
                spin={listReq.isLoading}
                className="me-sm-2"
              />
              <span className="d-none d-sm-inline">Refresh</span>
            </Button>
          </div>

          <div className="mym-stat-tabs is-three">
            {[
              {
                key: 'domains',
                label: 'Active domains',
                value: activeRows.length,
                tone: 'primary',
                icon: faAt
              },
              {
                key: 'groups',
                label: 'Groups',
                value: groupsCovered,
                tone: 'success',
                icon: faLayerGroup
              },
              {
                key: 'signups',
                label: 'Sign-ups',
                value: signUps,
                tone: 'purple',
                icon: faUserFriends
              }
            ].map(stat => (
              <div
                key={stat.key}
                className={classNames(
                  'mym-stat-tab is-static',
                  `mym-tone-${stat.tone}`
                )}
              >
                <ToneIcon icon={stat.icon} tone={stat.tone} />
                <span className="d-flex flex-column min-w-0">
                  <span className="mym-stat-tab-label">{stat.label}</span>
                  <span className="mym-stat-tab-count">
                    {loadedOnce ? (
                      stat.value
                    ) : (
                      <span
                        className="mym-count-placeholder"
                        aria-label="Loading"
                      />
                    )}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </Card.Body>
      </Card>

      <Card className="mb-3 shadow-sm">
        <Card.Header className="d-flex align-items-center gap-2 py-3">
          <ToneIcon icon={faPlus} tone="success" size="sm" />
          <h2 className="mb-0 fs-9 fw-bold text-900">
            Link a domain to a group
          </h2>
        </Card.Header>
        <Card.Body className="pt-0">
          <AddDomainForm
            groupOptions={groupOptions}
            activeRows={activeRows}
            onAdded={load}
          />
        </Card.Body>
      </Card>

      <AdvanceTableProvider {...table}>
        <Card className="mym-queue-table shadow-sm overflow-hidden mb-3">
          <Card.Header className="d-flex flex-wrap align-items-center justify-content-between gap-2 py-3">
            <div className="d-flex align-items-center gap-2">
              <ToneIcon icon={faLink} tone="primary" size="sm" />
              <h2 className="mb-0 fs-9 fw-bold text-900">Linked domains</h2>
              {loadedOnce && (
                <TonePill tone="primary">{visibleRows.length}</TonePill>
              )}
            </div>
            <div className="d-flex flex-wrap align-items-center gap-3">
              <Form.Select
                size="sm"
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                aria-label="Filter domains by status"
                style={{ width: '9.5rem' }}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="all">All domains</option>
              </Form.Select>
              <div style={{ width: '16rem', maxWidth: '100%' }}>
                <AdvanceTableSearchBox placeholder="Search domains or groups…" />
              </div>
            </div>
          </Card.Header>
          <Card.Body className="p-0">{content}</Card.Body>
        </Card>
      </AdvanceTableProvider>

      <DeactivateDomainModal
        show={deactivateTarget.show}
        row={deactivateTarget.row}
        groupName={
          deactivateTarget.row ? getGroupName(deactivateTarget.row) : ''
        }
        onHide={() => setDeactivateTarget(prev => ({ ...prev, show: false }))}
        onDeactivated={handleDeactivated}
      />
    </div>
  );
};

export default EmailDomains;
